/**
 * @fileoverview Simple lead form component for collecting basic lead information.
 * Handles form submission and validation for leads.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import './CallForm/CallForm.css';

/**
 * LeadForm component for collecting basic lead information.
 * Features:
 * - Legal name collection
 * - Email and phone validation
 * - Social media handles (without @ symbols)
 * - Form validation and error handling
 * - Simple submission to leads endpoint
 * 
 * @returns {React.ReactElement} The lead form interface
 */
const LeadForm = () => {
  const [formData, setFormData] = useState({
    First_Legal_Name: '',
    Last_Legal_Name: '',
    Email_Address: '',
    Phone: '',
    DOB: '',
    IG_Username: '',
    TT_Username: '',
    YT_Username: '',
    Snap_Username: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);


  /**
   * Handles form field changes with validation.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Remove @ symbols from social media usernames
    let processedValue = value;
    if (['IG_Username', 'TT_Username', 'YT_Username', 'Snap_Username'].includes(name)) {
      processedValue = value.replace(/@/g, '');
    }
    
    // Handle DOB formatting
    if (name === 'DOB') {
      processedValue = value.replace(/[^\d]/g, '').slice(0, 8);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Generate client_id when we have all required fields
    if ((name === 'First_Legal_Name' || name === 'Last_Legal_Name' || name === 'DOB') &&
        formData.First_Legal_Name && 
        formData.Last_Legal_Name && 
        (name === 'DOB' ? processedValue : formData.DOB)) {
      const firstInitial = formData.First_Legal_Name.charAt(0).toLowerCase();
      const lastInitial = formData.Last_Legal_Name.charAt(0).toLowerCase();
      const dob = name === 'DOB' ? processedValue : formData.DOB;
      const clientId = `${firstInitial}${lastInitial}${dob}`;
      
      setFormData(prev => ({
        ...prev,
        [name]: processedValue,
        client_id: clientId
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Validates form data before submission.
   * @returns {boolean} Whether the form is valid
   */
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.First_Legal_Name.trim()) {
      newErrors.First_Legal_Name = 'First name is required';
    }
    
    if (!formData.Last_Legal_Name.trim()) {
      newErrors.Last_Legal_Name = 'Last name is required';
    }
    
    if (!formData.Email_Address.trim()) {
      newErrors.Email_Address = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email_Address)) {
      newErrors.Email_Address = 'Please enter a valid email address';
    }
    
    if (!formData.Phone.trim()) {
      newErrors.Phone = 'Phone number is required';
    }
    
    if (!formData.DOB.trim()) {
      newErrors.DOB = 'Date of birth is required';
    } else if (formData.DOB.length !== 8) {
      newErrors.DOB = 'Date of birth must be in MMDDYYYY format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission to the leads endpoint.
   * @param {React.FormEvent<HTMLFormElement>} e - The form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Add source and collection fields
      const enrichedFormData = {
        ...formData,
        source: 'leads',
        collection: 'ClientInfo',
        Timezone: 'ET',
        // Set default values for metrics
        IG_Followers: 0,
        IG_Engagement: 0,
        IG_Rank: 0,
        IG_Views_Rank: 0,
        TT_Followers: 0,
        TT_Rank: 0,
        TT_Views_Rank: 0,
        TT_Engagement: 0,
        TT_Likes_Last_30_Days: 0,
        YT_Followers: 0,
        YT_Rank: 0,
        YT_Views_Rank: 0,
        YT_Engagement: 0,
        YT_Views: 0,
        Snap_Followers: "0",  // String for Snap followers
        algo_rank: 0
      };
      const response = await axios.post(API_ENDPOINTS.CALL_FORM, enrichedFormData);
      
      if (response.status === 200) {
        // Show success message
        alert('Thank you! Your submission has been received.');
        // Reset form
        setFormData({
          First_Legal_Name: '',
          Last_Legal_Name: '',
          Email_Address: '',
          Phone: '',
          DOB: '',
          IG_Username: '',
          TT_Username: '',
          YT_Username: '',
          Snap_Username: ''
        });
      }
    } catch (error) {
      console.error('Error submitting lead form:', error);
      setErrors({ submit: 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="call-form-page call-form">
      <div className="snapped-call-form__container">
        <h1 className="snapped-call-form__title">Snapped New Client Form</h1>
        <form onSubmit={handleSubmit}>
          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">Personal Information</h3>
            
            <input
              className={`snapped-call-form__input ${errors.First_Legal_Name ? 'snapped-call-form__input--error' : ''}`}
              type="text"
              name="First_Legal_Name"
              placeholder="First Legal Name *"
              value={formData.First_Legal_Name}
              onChange={handleChange}
              required
            />
            {errors.First_Legal_Name && (
              <span className="snapped-call-form__error-message">{errors.First_Legal_Name}</span>
            )}
            
            <input
              className={`snapped-call-form__input ${errors.Last_Legal_Name ? 'snapped-call-form__input--error' : ''}`}
              type="text"
              name="Last_Legal_Name"
              placeholder="Last Legal Name *"
              value={formData.Last_Legal_Name}
              onChange={handleChange}
              required
            />
            {errors.Last_Legal_Name && (
              <span className="snapped-call-form__error-message">{errors.Last_Legal_Name}</span>
            )}
            
            <input
              className={`snapped-call-form__input ${errors.Email_Address ? 'snapped-call-form__input--error' : ''}`}
              type="email"
              name="Email_Address"
              placeholder="Email Address *"
              value={formData.Email_Address}
              onChange={handleChange}
              required
            />
            {errors.Email_Address && (
              <span className="snapped-call-form__error-message">{errors.Email_Address}</span>
            )}
            
            <input
              className={`snapped-call-form__input ${errors.Phone ? 'snapped-call-form__input--error' : ''}`}
              type="tel"
              name="Phone"
              placeholder="Phone Number *"
              value={formData.Phone}
              onChange={handleChange}
              required
            />
            {errors.Phone && (
              <span className="snapped-call-form__error-message">{errors.Phone}</span>
            )}
            
            <input
              className={`snapped-call-form__input ${errors.DOB ? 'snapped-call-form__input--error' : ''}`}
              type="text"
              name="DOB"
              placeholder="Date of Birth (MMDDYYYY) *"
              value={formData.DOB}
              onChange={handleChange}
              maxLength={8}
              required
            />
            {errors.DOB && (
              <span className="snapped-call-form__error-message">{errors.DOB}</span>
            )}
          </div>

          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">Social Media Handles</h3>
            <p className="snapped-call-form__helper-text">Enter your social media handles without @ symbols</p>
            
            <input
              className="snapped-call-form__input"
              type="text"
              name="IG_Username"
              placeholder="Instagram Username (without @)"
              value={formData.IG_Username}
              onChange={handleChange}
            />
            
            <input
              className="snapped-call-form__input"
              type="text"
              name="TT_Username"
              placeholder="TikTok Username (without @)"
              value={formData.TT_Username}
              onChange={handleChange}
            />
            
            <input
              className="snapped-call-form__input"
              type="text"
              name="YT_Username"
              placeholder="YouTube Username (without @)"
              value={formData.YT_Username}
              onChange={handleChange}
            />
            
            <input
              className="snapped-call-form__input"
              type="text"
              name="Snap_Username"
              placeholder="Snapchat Username (without @)"
              value={formData.Snap_Username}
              onChange={handleChange}
            />
          </div>
          
          {errors.submit && (
            <div className="snapped-call-form__error-message">{errors.submit}</div>
          )}
          
          <button 
            type="submit" 
            className="snapped-call-form__submit-button"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LeadForm; 
